import React, { createContext, useContext, useMemo, useRef } from 'react';

export type SvgCacheEntry = {
  content?: string;
  error?: boolean;
  // An in-flight request promise to dedupe concurrent loads
  promise?: Promise<string> | null;
};

export interface SvgCacheContextValue {
  getCached: (path: string) => SvgCacheEntry | undefined;
  loadSvg: (path: string) => Promise<string>;
}

const SvgCacheContext = createContext<SvgCacheContextValue | undefined>(undefined);

export const SvgCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<Map<string, SvgCacheEntry>>(new Map());

  const value = useMemo<SvgCacheContextValue>(() => ({
    getCached: (path: string) => {
      return cacheRef.current.get(path);
    },
    loadSvg: async (path: string) => {
      // If we already have content, return it
      const existing = cacheRef.current.get(path);
      if (existing?.content) {
        return existing.content;
      }
      // If there is an in-flight request, return that
      if (existing?.promise) {
        return existing.promise;
      }
      // Start a new fetch and store the promise to dedupe
      const fetchPromise = fetch(path).then(async (res) => {
        if (!res.ok) {
          throw new Error('Failed to load SVG');
        }
        return res.text();
      });

      // Put or update entry with the promise
      cacheRef.current.set(path, { ...(existing || {}), promise: fetchPromise, error: false });

      try {
        const text = await fetchPromise;
        // Save content and clear promise
        cacheRef.current.set(path, { content: text, error: false, promise: null });
        return text;
      } catch (e) {
        console.error('Error loading SVG:', e);
        cacheRef.current.set(path, { content: undefined, error: true, promise: null });
        throw e;
      }
    }
  }), []);

  return (
    <SvgCacheContext.Provider value={value}>{children}</SvgCacheContext.Provider>
  );
};

export const useSvgCache = (): SvgCacheContextValue => {
  const ctx = useContext(SvgCacheContext);
  if (!ctx) {
    throw new Error('useSvgCache must be used within a SvgCacheProvider');
  }
  return ctx;
};
